import { db } from '../config/firebase';
import { collection, addDoc, Timestamp, doc, onSnapshot } from 'firebase/firestore';
import { ymd } from '../shared/dateUtils';
import { reportPDFBase64, ReportExportPayload } from '../utils/reportExport';

/**
 * Email Service
 * Handles sending reports and communications via email
 * Uses Firebase to queue emails for sending via Cloud Functions or backend service
 */

export interface EmailRecipient {
  email: string;
  name?: string;
}

export interface EmailAttachment {
  filename: string;
  content: string; // Base64 encoded
  type: string; // MIME type
}

export interface EmailTemplate {
  subject: string;
  body: string;
  footer: string;
}

export interface EmailData {
  to: EmailRecipient[];
  cc?: EmailRecipient[];
  bcc?: EmailRecipient[];
  subject: string;
  body: string;
  attachments?: EmailAttachment[];
  reportType: 'daily' | 'weekly' | 'payroll' | 'monthly' | 'barber-performance' | 'product-sales' | 'service-sales' | 'custom';
  reportData?: any; // The actual report data
  priority?: 'high' | 'normal' | 'low';
  scheduledFor?: Date; // For scheduled emails
}

export interface EmailHistory {
  id?: string;
  emailData: EmailData;
  sentAt: Date;
  sentBy: string;
  status: 'queued' | 'sent' | 'failed' | 'scheduled';
  error?: string;
}

class EmailService {
  /**
   * Send an email by adding it to Firebase queue
   * This will be picked up by a Cloud Function or backend service
   */
  async sendEmail(emailData: EmailData, sentBy: string): Promise<string> {
    try {
      // 🛑 STAGING GUARD (Jose 2026-10-29): NEVER let the queue processor DELIVER a real email from a staging
      // build. processEmailQueue acts only on status 'queued'/'scheduled', so writing it 'staging-suppressed'
      // means it's recorded for the UI but never sent. (Parity with the smsService staging guard — a real
      // client got a real text from staging; email must not do the same.)
      const _suppressed = process.env.REACT_APP_ENVIRONMENT === 'staging';
      console.log(_suppressed ? '📧 [EMAIL suppressed — staging]' : '📧 Queueing email for sending...', (emailData as any)?.to || '');

      // Add to Firebase emails collection
      const emailsRef = collection(db, 'emails');
      const emailDoc = await addDoc(emailsRef, {
        ...emailData,
        sentBy,
        status: _suppressed ? ('staging-suppressed' as any) : (emailData.scheduledFor ? 'scheduled' : 'queued'),
        createdAt: Timestamp.now(),
        sentAt: emailData.scheduledFor ? Timestamp.fromDate(emailData.scheduledFor) : null,
      });

      // Add to email history
      await this.addToHistory({
        emailData,
        sentBy,
        sentAt: emailData.scheduledFor || new Date(),
        status: emailData.scheduledFor ? 'scheduled' : 'queued',
      });

      console.log('✅ Email queued successfully:', emailDoc.id);
      return emailDoc.id;
    } catch (error) {
      console.error('❌ Error queueing email:', error);
      throw new Error('Failed to send email');
    }
  }

  /**
   * TRUTH IN REPORTING (Jose 2026-10-28): the modal used to toast "✅ sent" the instant the queue doc was
   * written — but the actual SendGrid send happens in a Cloud Function and can FAIL (e.g. "Maximum credits
   * exceeded"), which silently left reports undelivered. This watches the queued doc until the CF flips it
   * to 'sent' or 'failed' and returns the REAL outcome so the UI can tell the truth.
   */
  async waitForDelivery(emailId: string, timeoutMs = 20000): Promise<{ ok: boolean; status: string; error?: string }> {
    // On staging the email is intentionally never delivered (staging guard above), so don't make the UI hang
    // 20s waiting for a 'sent' flip — report a simulated success so the flow can be exercised. (Jose 2026-10-29)
    if (process.env.REACT_APP_ENVIRONMENT === 'staging') {
      return { ok: true, status: 'sent' };
    }
    return new Promise((resolve) => {
      let settled = false;
      const finish = (r: { ok: boolean; status: string; error?: string }) => { if (settled) return; settled = true; try { unsub(); } catch {} clearTimeout(timer); resolve(r); };
      const timer = setTimeout(() => finish({ ok: false, status: 'pending', error: 'Still sending — check Email Health in a moment.' }), timeoutMs);
      const unsub = onSnapshot(doc(db, 'emails', emailId), (snap) => {
        const s = (snap.data() as any)?.status;
        if (s === 'sent') finish({ ok: true, status: 'sent' });
        else if (s === 'failed') {
          const raw = (snap.data() as any)?.error || '';
          const friendly = /Maximum credits exceeded/i.test(String(raw))
            ? 'Email provider is out of sending credits (SendGrid). Upgrade the plan or export the PDF instead.'
            : (String(raw).slice(0, 160) || 'Send failed.');
          finish({ ok: false, status: 'failed', error: friendly });
        }
      }, () => finish({ ok: false, status: 'unknown', error: 'Could not confirm delivery.' }));
    });
  }

  /**
   * Add email to history for tracking
   */
  private async addToHistory(history: EmailHistory): Promise<void> {
    try {
      const historyRef = collection(db, 'emailHistory');
      await addDoc(historyRef, {
        ...history,
        sentAt: Timestamp.fromDate(history.sentAt),
      });
    } catch (error) {
      console.error('⚠️ Failed to add email to history:', error);
    }
  }

  /**
   * Get email templates for different report types
   */
  getEmailTemplate(reportType: EmailData['reportType'], reportData?: any): EmailTemplate {
    const templates: Record<EmailData['reportType'], EmailTemplate> = {
      daily: {
        subject: `Daily Report - ${reportData?.date || new Date().toLocaleDateString()}`,
        body: `
Hi,

Please find attached the daily performance report for ${reportData?.date || new Date().toLocaleDateString()}.

**Key Metrics:**
- Total Revenue: $${reportData?.revenue?.total?.toLocaleString() || '0.00'}
- Total Appointments: ${reportData?.appointments?.total || 0}
- Average Ticket: $${reportData?.metrics?.averageTicket?.toFixed(2) || '0.00'}
- Walk-ins: ${reportData?.appointments?.walkIns || 0}

Please review the attached report for detailed breakdowns.
        `.trim(),
        footer: `
---
This report was automatically generated by SLIK POS.
For questions, please contact your shop administrator.
        `.trim(),
      },
      weekly: {
        subject: `Weekly Report - ${reportData?.startDate || ''} to ${reportData?.endDate || ''}`,
        body: `
Hi,

Please find attached the weekly performance report.

**Period:** ${reportData?.startDate || ''} to ${reportData?.endDate || ''}

**Key Metrics:**
- Total Revenue: $${reportData?.totalRevenue?.toLocaleString() || '0.00'}
- Total Appointments: ${reportData?.totalAppointments || 0}
- Average Ticket: $${reportData?.averageTicket?.toFixed(2) || '0.00'}
- Growth: ${reportData?.comparison?.growthPercentage >= 0 ? '+' : ''}${reportData?.comparison?.growthPercentage?.toFixed(1) || '0.0'}%

Please review the attached report for detailed analysis.
        `.trim(),
        footer: `
---
This report was automatically generated by SLIK POS.
For questions, please contact your shop administrator.
        `.trim(),
      },
      payroll: {
        subject: `Payroll Report - ${reportData?.period?.startDate || ''} to ${reportData?.period?.endDate || ''}`,
        body: `
Hi,

Please find attached the payroll report for processing.

**Period:** ${reportData?.period?.startDate || ''} to ${reportData?.period?.endDate || ''}

**Summary:**
- Gross Revenue: $${reportData?.shopTotals?.grossRevenue?.toLocaleString() || '0.00'}
- Total Commissions: $${reportData?.shopTotals?.totalCommissionsPaid?.toLocaleString() || '0.00'}
- Shop Profit: $${reportData?.shopTotals?.shopProfit?.toLocaleString() || '0.00'}
- Barbers: ${reportData?.barbers?.length || 0}

Please process payroll for the barbers listed in the attached report.
        `.trim(),
        footer: `
---
This report was automatically generated by SLIK POS.
For questions, please contact your shop administrator.
        `.trim(),
      },
      monthly: {
        subject: `Monthly Report - ${reportData?.month || ''} ${reportData?.year || ''}`,
        body: `
Hi,

Please find attached the monthly performance report.

**Period:** ${reportData?.month || ''} ${reportData?.year || ''}

**Key Metrics:**
- Total Revenue: $${reportData?.revenue?.total?.toLocaleString() || '0.00'}
- Total Appointments: ${reportData?.appointments?.total || 0}
- Average Ticket: $${reportData?.metrics?.averageTicket?.toFixed(2) || '0.00'}
- Growth: ${reportData?.comparison?.growthPercentage >= 0 ? '+' : ''}${reportData?.comparison?.growthPercentage?.toFixed(1) || '0.0'}%

Please review the attached report for comprehensive monthly analysis.
        `.trim(),
        footer: `
---
This report was automatically generated by SLIK POS.
For questions, please contact your shop administrator.
        `.trim(),
      },
      'barber-performance': {
        subject: `Barber Performance Report - ${reportData?.barberName || ''}`,
        body: `
Hi,

Please find attached the performance report for ${reportData?.barberName || ''}.

**Period:** ${reportData?.period?.startDate || ''} to ${reportData?.period?.endDate || ''}

**Performance Metrics:**
- Total Revenue: $${reportData?.revenue?.total?.toLocaleString() || '0.00'}
- Total Appointments: ${reportData?.appointments?.total || 0}
- Average Ticket: $${reportData?.metrics?.averageTicket?.toFixed(2) || '0.00'}
- Retention Rate: ${reportData?.metrics?.retentionRate?.toFixed(1) || '0.0'}%

Please review the attached report for detailed performance analysis.
        `.trim(),
        footer: `
---
This report was automatically generated by SLIK POS.
For questions, please contact your shop administrator.
        `.trim(),
      },
      'product-sales': {
        subject: `Product Sales Report - ${reportData?.period || ''}`,
        body: `
Hi,

Please find attached the product sales report.

**Period:** ${reportData?.period || ''}

**Summary:**
- Product Revenue: $${Number(reportData?.productRevenue ?? reportData?.totalRevenue ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
- Service Revenue: $${Number(reportData?.serviceRevenue ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
- Retail Attachment: ${Number(reportData?.attachPct ?? 0).toFixed(1)}%${reportData?.projected != null ? `
- Projected (period end): $${Number(reportData.projected).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
- Top Product: ${reportData?.topProduct || 'N/A'}

Please review the attached report for the detailed product sales breakdown.
        `.trim(),
        footer: `
---
This report was automatically generated by SLIK POS.
For questions, please contact your shop administrator.
        `.trim(),
      },
      'service-sales': {
        subject: `Service Sales Report - ${reportData?.period || ''}`,
        body: `
Hi,

Please find attached the service sales report.

**Period:** ${reportData?.period || ''}

**Summary:**
- Total Service Revenue: $${reportData?.totalRevenue?.toLocaleString() || '0.00'}
- Total Bookings: ${reportData?.totalBookings || 0}
- Average Service Price: $${reportData?.averageServicePrice?.toFixed(2) || '0.00'}
- Most Booked Service: ${reportData?.topService?.name || 'N/A'}
- Lost Revenue: $${reportData?.lostRevenue?.total?.toLocaleString() || '0.00'}

Please review the attached report for detailed service performance and lost revenue analysis.
        `.trim(),
        footer: `
---
This report was automatically generated by SLIK POS.
For questions, please contact your shop administrator.
        `.trim(),
      },
      custom: {
        subject: 'SLIK POS Report',
        body: `
Hi,

Please find attached the requested report.
        `.trim(),
        footer: `
---
This report was automatically generated by SLIK POS.
For questions, please contact your shop administrator.
        `.trim(),
      },
    };

    return templates[reportType];
  }

  /**
   * Generate HTML email body from template
   */
  generateEmailHTML(template: EmailTemplate): string {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
    }
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 30px;
      border-radius: 10px 10px 0 0;
      text-align: center;
    }
    .header h1 {
      margin: 0;
      font-size: 24px;
    }
    .content {
      background: white;
      padding: 30px;
      border: 1px solid #e5e7eb;
      border-top: none;
    }
    .content pre {
      white-space: pre-wrap;
      font-family: inherit;
    }
    .footer {
      background: #f9fafb;
      padding: 20px;
      border-radius: 0 0 10px 10px;
      text-align: center;
      color: #6b7280;
      font-size: 14px;
      border: 1px solid #e5e7eb;
      border-top: none;
    }
    .button {
      display: inline-block;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 12px 24px;
      text-decoration: none;
      border-radius: 6px;
      margin: 20px 0;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>SLIK POS Report</h1>
  </div>
  <div class="content">
    <pre>${template.body}</pre>
  </div>
  <div class="footer">
    <pre>${template.footer}</pre>
  </div>
</body>
</html>
    `.trim();
  }

  /**
   * Default recipients = the last set the owner actually sent to (persisted). This replaced a hardcoded
   * `accountant@slikpos.com` placeholder that silently swallowed sends (Jose 2026-09-09: emailed himself,
   * received nothing — it had gone to the fake address). Empty first time → the modal makes them add one.
   */
  getDefaultRecipients(): EmailRecipient[] {
    try {
      const saved = localStorage.getItem('slik_report_recipients');
      if (saved) { const arr = JSON.parse(saved); if (Array.isArray(arr) && arr.length) return arr; }
    } catch { /* ignore */ }
    return [];
  }

  /** Remember the recipients the owner just sent to, so next time they're pre-filled. */
  saveDefaultRecipients(recipients: EmailRecipient[]): void {
    try { localStorage.setItem('slik_report_recipients', JSON.stringify(recipients || [])); } catch { /* ignore */ }
    this.addToRecipientBook(recipients);
  }

  /** Saved address book — EVERY recipient ever added/sent (deduped), so a deleted one is one tap to
   *  re-add. Separate from getDefaultRecipients (which is just the last-sent prefill). (Jose 2026-10-15) */
  getRecipientBook(): EmailRecipient[] {
    try {
      const saved = localStorage.getItem('slik_report_recipient_book');
      if (saved) { const arr = JSON.parse(saved); if (Array.isArray(arr)) return arr; }
    } catch { /* ignore */ }
    return [];
  }

  addToRecipientBook(recipients: EmailRecipient[]): void {
    try {
      const byEmail = new Map(this.getRecipientBook().map((r) => [r.email.toLowerCase(), r]));
      for (const r of recipients || []) {
        if (!r?.email) continue;
        const key = r.email.toLowerCase();
        byEmail.set(key, { email: r.email.trim(), name: r.name || byEmail.get(key)?.name });
      }
      localStorage.setItem('slik_report_recipient_book', JSON.stringify(Array.from(byEmail.values())));
    } catch { /* ignore */ }
  }

  removeFromRecipientBook(email: string): void {
    try {
      const book = this.getRecipientBook().filter((r) => r.email.toLowerCase() !== (email || '').toLowerCase());
      localStorage.setItem('slik_report_recipient_book', JSON.stringify(book));
    } catch { /* ignore */ }
  }

  /**
   * Validate email address
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Generate PDF from report data (client-side)
   * This is a placeholder - will need a proper PDF library like jsPDF or react-pdf
   */
  async generatePDF(reportType: EmailData['reportType'], reportData: any, payload?: ReportExportPayload): Promise<EmailAttachment> {
    // Real branded PDF via the shared report exporter (was a stub that base64'd JSON → unopenable).
    // If the caller passed the report's own export payload (accountant-grade table), use it verbatim;
    // otherwise derive a minimal real payload from reportData so the attachment still opens.
    const p: ReportExportPayload = payload || {
      filename: `${reportType}-report-${ymd()}`,
      title: `${String(reportType).replace(/(^|[-_ ])(\w)/g, (_m, s, c) => (s ? ' ' : '') + c.toUpperCase())} Report`,
      subtitle: reportData?.period ? `${reportData.period.startDate} – ${reportData.period.endDate}` : undefined,
      summary: Object.entries(reportData?.shopTotals || reportData?.summary || {})
        .filter(([, v]) => typeof v === 'number' || typeof v === 'string')
        .slice(0, 12)
        .map(([k, v]) => ({ label: k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()), value: String(v) })),
      columns: [{ key: 'k', label: 'Field' }, { key: 'v', label: 'Value' }],
      rows: [],
    };
    return {
      filename: `${p.filename}.pdf`,
      content: reportPDFBase64(p),
      type: 'application/pdf',
    };
  }
}

// Export singleton instance
export const emailService = new EmailService();
